import type { LicenseTier } from '@/mocks/saLicensing';
import { licenseTypeIcons, licenseTypeBadge, formatStorage, formatBandwidth, formatUsers, formatRadios } from '@/mocks/saLicensing';

interface LicenseListItemProps {
  license: LicenseTier;
  onEdit: (license: LicenseTier) => void;
  onDuplicate: (license: LicenseTier) => void;
  onToggleActive: (license: LicenseTier) => void;
  onToggleVisible: (license: LicenseTier) => void;
  onPreview: (license: LicenseTier) => void;
  onCloneConfig: (license: LicenseTier) => void;
}

function priceLabel(lic: LicenseTier): string {
  if (lic.priceMonthly > 0) return `$${lic.priceMonthly}/mes`;
  if (lic.priceAnnual > 0) return `$${lic.priceAnnual}/año`;
  if (lic.priceLifetime > 0) return `$${lic.priceLifetime} único`;
  return 'Gratis';
}

export default function LicenseListItem({
  license,
  onEdit,
  onDuplicate,
  onToggleActive,
  onToggleVisible,
  onPreview,
  onCloneConfig,
}: LicenseListItemProps) {
  return (
    <div className={`flex flex-col lg:flex-row lg:items-center gap-3 p-4 rounded-2xl border transition-all ${
      license.active ? 'bg-background-50 border-background-200 hover:border-background-300' : 'bg-background-50 border-background-200 opacity-70'
    }`}>
      {/* Left: basic info */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${license.color}14` }}>
          <i className={`${licenseTypeIcons[license.type]} text-sm`} style={{ color: license.color }}></i>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-foreground-800 text-xs font-bold">{license.name}</span>
            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${licenseTypeBadge[license.type]}`}>
              {license.type}
            </span>
            {!license.active && <span className="px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-semibold">Inactiva</span>}
            {!license.visible && <span className="px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[9px] font-semibold">Oculta</span>}
          </div>
          <p className="text-foreground-400 text-[10px] mt-0.5 line-clamp-1">{license.description}</p>
        </div>
      </div>

      {/* Middle: metrics */}
      <div className="flex items-center gap-4 flex-wrap text-[10px]">
        <div className="text-center">
          <p className="text-foreground-400 text-[9px]">Precio</p>
          <p className="text-foreground-800 font-bold">{priceLabel(license)}</p>
        </div>
        <div className="text-center">
          <p className="text-foreground-400 text-[9px]">Radios</p>
          <p className="text-foreground-700 font-mono">{formatRadios(license.maxRadios)}</p>
        </div>
        <div className="text-center">
          <p className="text-foreground-400 text-[9px]">Usuarios</p>
          <p className="text-foreground-700 font-mono">{formatUsers(license.maxUsers)}</p>
        </div>
        <div className="text-center">
          <p className="text-foreground-400 text-[9px]">Storage</p>
          <p className="text-foreground-700 font-mono">{formatStorage(license.storageGB)}</p>
        </div>
        <div className="text-center">
          <p className="text-foreground-400 text-[9px]">BW</p>
          <p className="text-foreground-700 font-mono">{formatBandwidth(license.bandwidthGB)}</p>
        </div>
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
        <button onClick={() => onEdit(license)} className="p-1.5 rounded-lg bg-background-100 text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer" title="Editar">
          <i className="ri-edit-line text-xs"></i>
        </button>
        <button onClick={() => onDuplicate(license)} className="p-1.5 rounded-lg bg-background-100 text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer" title="Duplicar">
          <i className="ri-file-copy-line text-xs"></i>
        </button>
        <button onClick={() => onPreview(license)} className="p-1.5 rounded-lg bg-accent-50 text-accent-600 hover:bg-accent-100 transition-colors cursor-pointer" title="Preview">
          <i className="ri-eye-line text-xs"></i>
        </button>
        <button onClick={() => onCloneConfig(license)} className="p-1.5 rounded-lg bg-violet-50 text-violet-600 hover:bg-violet-100 transition-colors cursor-pointer" title="Clonar configuración">
          <i className="ri-git-branch-line text-xs"></i>
        </button>
        <button onClick={() => onToggleActive(license)} className={`p-1.5 rounded-lg transition-colors cursor-pointer ${license.active ? 'bg-amber-50 text-amber-600 hover:bg-amber-100' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'}`} title={license.active ? 'Desactivar' : 'Activar'}>
          <i className={license.active ? 'ri-pause-circle-line text-xs' : 'ri-play-circle-line text-xs'}></i>
        </button>
        <button onClick={() => onToggleVisible(license)} className={`p-1.5 rounded-lg transition-colors cursor-pointer ${license.visible ? 'bg-amber-50 text-amber-600 hover:bg-amber-100' : 'bg-sky-50 text-sky-600 hover:bg-sky-100'}`} title={license.visible ? 'Ocultar' : 'Publicar'}>
          <i className={license.visible ? 'ri-eye-off-line text-xs' : 'ri-eye-line text-xs'}></i>
        </button>
      </div>
    </div>
  );
}